import React, { useEffect } from 'react';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import StoreHero from '../components/home/StoreHero';
import ProductShowcase from '../components/home/ProductShowcase';
import Features from '../components/home/Features';
import CheckoutSteps from '../components/home/CheckoutSteps';
import Audience from '../components/home/Audience';
import Pricing from '../components/home/Pricing';
import Testimonials from '../components/home/Testimonials';
import LatestPosts from '../components/home/LatestPosts';
import FAQNew, { FAQS } from '../components/home/FAQNew';
import CTA from '../components/home/CTA';
import { siteConfig } from '../config/site';

const HomePage: React.FC = () => {
  // Inject JSON-LD schemas for SEO: Store + WebSite + FAQPage + Product
  useEffect(() => {
    const store = {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      logo: `${siteConfig.domain}/logo512.png`,
      description: siteConfig.brand.description,
      email: siteConfig.contact.orderEmail,
      contactPoint: [{
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: siteConfig.contact.phoneDisplay,
        availableLanguage: ['English'],
      }],
    };
    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteConfig.domain}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const product = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${siteConfig.brand.name} — Flash USDT Software License`,
      description: siteConfig.brand.description,
      brand: { '@type': 'Brand', name: siteConfig.brand.name },
      category: 'Software · Cryptocurrency Tools',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '15',
        highPrice: '5000',
        offerCount: '6',
        url: `${siteConfig.domain}/pricing`,
      },
    };
    const tags: HTMLScriptElement[] = [];
    for (const [id, obj] of [
      ['ld-store', store],
      ['ld-website', website],
      ['ld-faq', faq],
      ['ld-product', product],
    ] as const) {
      let s = document.getElementById(id) as HTMLScriptElement | null;
      if (!s) {
        s = document.createElement('script');
        s.id = id; s.type = 'application/ld+json';
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(obj);
      tags.push(s);
    }
    return () => tags.forEach((t) => t.remove());
  }, []);

  return (
    <Layout>
      <SEO
        title={`${siteConfig.brand.name} — Buy Flash USDT Software Online (TRC-20, ERC-20, BEP-20)`}
        description={`Buy licensed flash USDT software at ${siteConfig.brand.name}. Instant email delivery, multi-chain TRC-20 / ERC-20 / BEP-20, 50+ wallets, 24/7 WhatsApp support, 365-day flash validity, plans from $15.`}
        keywords="usdt flasher store, buy flash USDT, flash USDT software store, flash USDT for sale, TRC-20 flash USDT, ERC-20 flash USDT, BEP-20 flash USDT, USDT flasher license, tether flashing tool, buy USDT flasher online"
      />
      <StoreHero />
      <ProductShowcase />
      <Features />
      <CheckoutSteps />
      <Audience />
      <Pricing />
      <Testimonials />
      <LatestPosts />
      <FAQNew />
      <CTA />
    </Layout>
  );
};

export default HomePage;
